import {
  Document, Packer, Paragraph, TextRun,
  Table, TableRow, TableCell,
  AlignmentType, WidthType, BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';
import { LETTERHEAD_SECTION_PROPS, buildHeaders, buildFooters } from './documents/letterhead';

// ── CONSTANTS ─────────────────────────────────────────────────
const FONT    = 'Arial';
const C_NAVY  = '1F3864';
const C_GREY  = '666666';
const C_BORDER = 'B8CCE4';
const C_HDR   = 'D9E2F3';
const C_GREEN = '1E6B3C';
const C_AMBER = '7A4F00';

const pt   = (n) => n * 2;
const THIN  = { style: BorderStyle.SINGLE, size: 4,  color: C_BORDER };
const NONE  = { style: BorderStyle.NONE,   size: 0,  color: 'FFFFFF' };
const allNone = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const allThin = { top: THIN, bottom: THIN, left: THIN, right: THIN };

const blank = (before = 60, after = 0) =>
  new Paragraph({ children: [new TextRun('')], spacing: { before, after } });

// ── TYPOGRAPHY ─────────────────────────────────────────────────
function sectionHeader(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: pt(12), color: C_NAVY, font: FONT })],
    spacing: { before: 260, after: 80 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: C_NAVY } },
  });
}

function subHeader(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: pt(10), color: C_NAVY, font: FONT })],
    spacing: { before: 160, after: 60 },
  });
}

function bodyText(text, italic = false) {
  return new Paragraph({
    children: [new TextRun({ text: text || '—', size: pt(9), font: FONT, italics: italic, color: text ? C_NAVY : C_GREY })],
    spacing: { before: 40, after: 40 },
  });
}

function textBox(content) {
  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [10800],
    rows: [new TableRow({
      children: [new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: content || '', size: pt(9), font: FONT })],
          spacing: { before: 60, after: 60 },
        })],
        borders: allThin,
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
      })],
    })],
    borders: { insideH: NONE, insideV: NONE },
  });
}

// ── KV TABLE (label | value pairs) ────────────────────────────
function kvRow(pairs) {
  const cells = pairs.map(([label, value]) =>
    new TableCell({
      children: [new Paragraph({
        children: [
          new TextRun({ text: `${label}:  `, bold: true, size: pt(9), font: FONT, color: C_GREY }),
          new TextRun({ text: value || '—', size: pt(9), font: FONT, color: C_NAVY }),
        ],
        spacing: { before: 40, after: 40 },
      })],
      borders: allNone,
      margins: { top: 40, bottom: 40, left: 80, right: 80 },
      width: { size: Math.floor(10800 / pairs.length), type: WidthType.DXA },
    })
  );
  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: pairs.map(() => Math.floor(10800 / pairs.length)),
    rows: [new TableRow({ children: cells })],
    borders: { insideH: NONE, insideV: NONE, top: NONE, bottom: NONE, left: NONE, right: NONE },
  });
}

// ── RATING TABLE ───────────────────────────────────────────────
function ratingTable(rows) {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: 'Item', bold: true, size: pt(9), font: FONT, color: C_NAVY })], spacing: { before: 40, after: 40 } })],
        shading: { fill: C_HDR },
        borders: allThin,
        margins: { top: 60, bottom: 60, left: 100, right: 100 },
        width: { size: 8000, type: WidthType.DXA },
      }),
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: 'Rating', bold: true, size: pt(9), font: FONT, color: C_NAVY })], spacing: { before: 40, after: 40 }, alignment: AlignmentType.CENTER })],
        shading: { fill: C_HDR },
        borders: allThin,
        margins: { top: 60, bottom: 60, left: 100, right: 100 },
        width: { size: 2800, type: WidthType.DXA },
      }),
    ],
  });

  const dataRows = rows.map(([label, value]) =>
    new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: label, size: pt(9), font: FONT })], spacing: { before: 40, after: 40 } })],
          borders: allThin,
          margins: { top: 40, bottom: 40, left: 100, right: 100 },
          width: { size: 8000, type: WidthType.DXA },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: String(value || '—'), size: pt(9), font: FONT, bold: true })], spacing: { before: 40, after: 40 }, alignment: AlignmentType.CENTER })],
          borders: allThin,
          margins: { top: 40, bottom: 40, left: 100, right: 100 },
          width: { size: 2800, type: WidthType.DXA },
        }),
      ],
    })
  );

  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [8000, 2800],
    rows: [headerRow, ...dataRows],
  });
}

// ── CHECKLIST ITEM ─────────────────────────────────────────────
function checkItem(label, checked) {
  const mark = checked ? '☑' : '☐';
  return new Paragraph({
    children: [
      new TextRun({ text: `${mark}  `, size: pt(10), font: 'MS Gothic' }),
      new TextRun({ text: label, size: pt(9), font: FONT }),
    ],
    spacing: { before: 40, after: 40 },
  });
}

// ── SIGNATURE LINE ─────────────────────────────────────────────
function sigLine(label, name, date) {
  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [7000, 3800],
    rows: [
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: name || '', size: pt(13), font: 'Arial', italics: true, color: C_GREEN })], spacing: { before: 40, after: 20 } })],
            borders: { ...allNone, bottom: THIN },
            width: { size: 7000, type: WidthType.DXA },
            margins: { bottom: 40 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: date || '', size: pt(9), font: FONT, color: C_NAVY })], spacing: { before: 40, after: 20 }, alignment: AlignmentType.CENTER })],
            borders: { ...allNone, bottom: THIN },
            width: { size: 3800, type: WidthType.DXA },
            margins: { bottom: 40 },
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: label, size: pt(8), font: FONT, color: C_GREY })], spacing: { before: 30, after: 0 } })],
            borders: allNone,
            width: { size: 7000, type: WidthType.DXA },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Date', size: pt(8), font: FONT, color: C_GREY })], spacing: { before: 30, after: 0 }, alignment: AlignmentType.CENTER })],
            borders: allNone,
            width: { size: 3800, type: WidthType.DXA },
          }),
        ],
      }),
    ],
    borders: { insideH: NONE, insideV: NONE, top: NONE, bottom: NONE, left: NONE, right: NONE },
  });
}

// ── FORMAT HELPERS ─────────────────────────────────────────────
const fmt = (v) => v ? String(v).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};
const stars = (n) => n ? `${n}/5` : '—';

// ── MAIN EXPORT ────────────────────────────────────────────────
export async function generateSupervisorVisitDoc({ visit, client }) {
  const v = visit  || {};
  const c = client || {};

  const visitDate   = fmtDate(v.visit_date);
  const clientName  = c.first_name ? `${c.first_name} ${c.last_name}` : (v.client_name || '');
  const visitType   = {
    initial_visit: 'Initial Visit', gapp_pss_biweekly: 'GAPP — PSS/Family Caregiver',
    gapp_rn_monthly: 'GAPP — RN Monthly Assessment', scheduled: 'Scheduled',
    unannounced: 'Unannounced', follow_up: 'Follow-Up',
  }[v.visit_type] || fmt(v.visit_type);

  const children = [

    // ── TITLE (company branding lives in the letterhead header) ──
    new Paragraph({
      children: [new TextRun({ text: 'Supervisory Visit Report', bold: true, size: pt(14), color: C_NAVY, font: FONT })],
      spacing: { before: 0, after: 80 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `Rule 111-8-65-.10 — Georgia Home Health Agency Regulations`, italics: true, size: pt(9), color: C_GREY, font: FONT })],
      spacing: { before: 0, after: 100 },
    }),

    // ── VISIT HEADER INFO ────────────────────────────────────
    kvRow([['Client', clientName], ['Client ID', c.client_id || v.client_ref || '—']]),
    blank(30),
    kvRow([['Visit Date', visitDate], ['Visit Type', visitType]]),
    blank(30),
    kvRow([['Supervisor', v.supervisor_name || '—'], ['Duration', v.visit_duration_minutes ? `${v.visit_duration_minutes} minutes` : '—']]),
    blank(30),
    kvRow([['Assigned Nurse', v.assigned_nurse_name || '—'], ['Nurse Present', v.nurse_present === true ? 'Yes' : v.nurse_present === false ? 'No' : '—']]),
    blank(30),
    kvRow([['Next Visit Due', fmtDate(v.next_visit_due)], ['Completed By', v.completed_by || '—']]),
    blank(60),

    // ════════════════════════════════════════════════════════
    // SECTION 1 — CLINICAL OBSERVATION
    // ════════════════════════════════════════════════════════
    sectionHeader('SECTION 1 — CLINICAL OBSERVATION'),

    subHeader('1.1  Patient Condition'),
    kvRow([['Overall Condition', fmt(v.overall_condition)], ['Vital Signs', v.vital_signs_status || '—']]),
    blank(40),
    kvRow([['Skin Integrity', fmt(v.skin_integrity)], ['Equipment Functioning', fmt(v.equipment_functioning)]]),
    blank(60),

    subHeader('1.2  Medication Review'),
    kvRow([['Technique', fmt(v.medication_technique)], ['Medication List Current', v.medication_list_current || '—']]),
    blank(60),

    subHeader('1.3  Clinical Notes'),
    textBox(v.clinical_notes || ''),
    blank(80),

    // ════════════════════════════════════════════════════════
    // SECTION 2 — NURSE PERFORMANCE
    // ════════════════════════════════════════════════════════
    sectionHeader('SECTION 2 — NURSE PERFORMANCE REVIEW'),

    subHeader('2.1  Compliance Checklist'),
    checkItem('Documentation current and complete', v.documentation_current),
    checkItem('Care plan followed as written', v.care_plan_followed),
    checkItem('Infection control measures observed', v.infection_control_observed),
    checkItem('Emergency protocols reviewed with family', v.emergency_protocol_reviewed),
    checkItem('Positive family interaction noted', v.family_interaction_ok),
    checkItem('Caregiver education ongoing', v.caregiver_education_ongoing),
    blank(60),

    subHeader('2.2  Performance Rating'),
    bodyText(`Overall Rating: ${fmt(v.performance_rating)}`),
    blank(40),

    subHeader('2.3  Strengths Observed'),
    textBox(v.strengths || ''),
    blank(40),

    subHeader('2.4  Areas for Improvement'),
    textBox(v.improvements_needed || ''),
    blank(40),

    ...(v.competency_followup?.length ? [
      subHeader('2.5  Competency Follow-Up Required'),
      bodyText(Array.isArray(v.competency_followup) ? v.competency_followup.join(', ') : v.competency_followup),
      blank(40),
    ] : []),

    ...(v.followup_visit_required ? [
      subHeader('2.6  Follow-Up Visit'),
      bodyText(`Required — scheduled for ${fmtDate(v.followup_visit_date)}`),
      blank(40),
    ] : []),

    blank(40),

    // ════════════════════════════════════════════════════════
    // SECTION 3 — FAMILY SATISFACTION
    // ════════════════════════════════════════════════════════
    sectionHeader('SECTION 3 — FAMILY SATISFACTION'),

    subHeader('3.1  Satisfaction Ratings'),
    ratingTable([
      ['Overall Satisfaction',      stars(v.satisfaction_overall)],
      ['Nurse Consistency',         stars(v.satisfaction_consistency)],
      ['Communication',             stars(v.satisfaction_communication)],
      ['Care Plan Understanding',   stars(v.satisfaction_care_plan)],
    ]),
    blank(60),

    ...(v.family_concerns ? [
      subHeader('3.2  Family Concerns / Comments'),
      textBox(v.family_concerns),
      blank(40),
    ] : []),

    blank(40),

    // ════════════════════════════════════════════════════════
    // SECTION 4 — PHYSICIAN SUMMARY
    // ════════════════════════════════════════════════════════
    sectionHeader('SECTION 4 — PHYSICIAN SUMMARY REPORT'),

    subHeader('4.1  Summary Details'),
    kvRow([['Physician', v.physician_name || '—'], ['Delivery Method', fmt(v.physician_delivery_method)]]),
    blank(30),
    kvRow([['Fax / Email', v.physician_fax_email || '—'], ['Reporting Period', `${fmtDate(v.summary_period_start)} → ${fmtDate(v.summary_period_end)}`]]),
    blank(60),

    subHeader('4.2  Clinical Summary'),
    textBox(v.final_summary || ''),
    blank(40),

    ...(v.recommended_physician_actions ? [
      subHeader('4.3  Recommended Physician Actions'),
      textBox(v.recommended_physician_actions),
      blank(40),
    ] : []),

    blank(80),

    // ════════════════════════════════════════════════════════
    // ATTESTATION & SIGNATURE
    // ════════════════════════════════════════════════════════
    sectionHeader('ATTESTATION & SIGNATURE'),

    new Paragraph({
      children: [new TextRun({
        text: `I, ${v.completed_by || v.supervisor_name || '_______________'}, attest that I conducted this supervisory visit in person on ${visitDate}, that all findings documented herein are accurate, and that the physician summary has been reviewed and is ready to send.`,
        size: pt(9), font: FONT, color: C_NAVY,
      })],
      spacing: { before: 60, after: 80 },
    }),

    blank(60),
    sigLine('Supervisor / Completing Staff', v.completed_by || v.supervisor_name || '', visitDate),
    blank(80),
  ];

  const doc = new Document({
    sections: [{
      properties: LETTERHEAD_SECTION_PROPS,
      headers: buildHeaders(),
      footers: buildFooters({
        note: 'Supervisory Visit Report  ·  Rule 111-8-65-.10 — Georgia Home Health Agency Regulations  ·  Generated by CAERA',
      }),
      children,
    }],
    styles: {
      default: { document: { run: { font: FONT, size: pt(9) } } },
    },
  });

  return await Packer.toBlob(doc);
}

export async function downloadSupervisorVisitDoc({ visit, client }) {
  const blob = await generateSupervisorVisitDoc({ visit, client });
  const clientName = client
    ? `${client.first_name}_${client.last_name}`
    : (visit?.client_name || 'Client').replace(/\s+/g, '_');
  const date = visit?.visit_date || new Date().toISOString().split('T')[0];
  saveAs(blob, `SupervisoryVisit_${clientName}_${date}.docx`);
}
